import { useStoreApi } from '@givebacks/api/store';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QueryPagination, createQueryOptions } from 'core/Packages';

import { storeKeys } from '../../../query-key-factory';
import { PeerTypes } from '../../types';
import {
  PeerParticipantResponse,
  PeerResponse,
  PeerSearch,
  PeerTeamResponse,
  PeersResponse,
} from './types';
import { campaignPeerWithType } from './utils';

const useCurrentCauseQueryString = (causeId?: string) => {
  return `cause_id=${causeId}`;
};

// Return either a participant, or team based on the peerType
const useShowCampaignPeer = <T extends PeerTypes>({
  causeId,
  campaignId,
  participantId,
  peerType,
  queryOptions,
}: {
  causeId?: string;
  campaignId?: string;
  participantId?: string;
  peerType: T;
  queryOptions?: PeerSearch;
}) => {
  const StoreApi = useStoreApi();
  const causeQuery = useCurrentCauseQueryString(causeId);
  const { queryString, queryObject } = createQueryOptions<PeerSearch>({
    search: queryOptions,
    existingSearchString: causeQuery,
  });

  const getParticipant = async () => {
    const { data } = await StoreApi.get<PeerResponse<T>>(
      `/store_items/${campaignId}/${peerType}/${participantId}?${queryString}`
    );
    const peerData = data[
      peerType == 'participants' ? 'participant' : 'team'
    ] as T extends 'participants'
      ? PeerParticipantResponse['participant']
      : PeerTeamResponse['team'];
    return campaignPeerWithType(peerData);
  };

  const isEnabled = !!causeId && !!campaignId && !!participantId && !!peerType;

  const key = isEnabled
    ? [
        ...storeKeys.id(causeId).items.campaigns.id(campaignId)[peerType].id(participantId).detail,
        queryObject,
      ]
    : [];
  return useQuery({
    queryKey: key,
    queryFn: getParticipant,
    enabled: isEnabled,
  });
};

type GetCampaignsContext = {
  meta?: {
    campaignId?: string;
    peer_type?: PeerTypes;
    queryString?: string;
    causeId?: string;
  };
};

const useGetPeers = <T extends PeerTypes | undefined>() => {
  const queryClient = useQueryClient();
  const StoreApi = useStoreApi();
  const getPeers = async (context: GetCampaignsContext) => {
    const { campaignId, peer_type, queryString, causeId } = context.meta || {};
    const { data } = await StoreApi.get<PeersResponse<T>>(
      `store_items/${campaignId}/${peer_type}?${queryString}`
    );

    const peers = data?.[peer_type == 'participants' ? 'participants' : 'teams'];

    peers?.forEach((peer) => {
      const isEnabled = !!causeId && !!campaignId && !!peer.uuid && !!peer_type;
      if (!isEnabled) return;
      queryClient.setQueryData(
        storeKeys.id(causeId).items.campaigns.id(campaignId)[peer_type].id(peer.uuid).detail,
        peer
      );
    });

    return data;
  };
  return getPeers;
};

const useListCampaignPeers = <T extends PeerTypes, U = PeersResponse<T>>({
  pagination,
  queryOptions,
  peer_type,
  select,
  causeId,
  campaignId,
}: {
  pagination?: QueryPagination;
  queryOptions?: PeerSearch;
  peer_type: T;
  select?: (data?: PeersResponse<T>) => U;
  causeId?: string;
  campaignId?: string;
}) => {
  const causeQuery = useCurrentCauseQueryString(causeId);

  const getCampaignPeers = useGetPeers<T>();
  const { queryString, queryObject, queryPagination } = createQueryOptions<PeerSearch>({
    search: queryOptions,
    pagination,
    existingSearchString: causeQuery,
  });
  const peerKey = peer_type == 'participants' ? 'participants' : 'teams';

  const key =
    campaignId && causeId && peer_type
      ? [
          ...storeKeys.id(causeId).items.campaigns.id(campaignId)[peerKey].all,
          queryObject,
          queryPagination,
        ]
      : [];
  return useQuery({
    queryKey: key,
    queryFn: getCampaignPeers,
    enabled: !!campaignId && !!causeId && !!peer_type,
    meta: { campaignId, peer_type, queryString, causeId },
    // placeholderData: keepPreviousData,
    select,
  });
};

export { useGetPeers, useListCampaignPeers, useShowCampaignPeer };
